// B1.4: move-only from client.ts.
import { ApiClientCore, ApiError, buildQueryString } from './http/core';

export class OtherApi extends ApiClientCore {
  async addRequestParticipant(
    requestId: number, 
    data: { 
      user_id?: number; 
      employee_id?: number; 
      role: string; 
      custom_permissions?: Record<string, boolean>;
      expires_at?: string;
    }
  ): Promise<any> {
    return this.post(`/api/v1/consumables/requests/${requestId}/participants`, data);
  }
  async createAbsence(data: any): Promise<any> {
    return this.post('/api/v1/absences', data);
  }
  async createAccessLevel(data: { name: string; base_role: string; modules?: Record<string, unknown> }): Promise<any> {
    return this.post('/api/v1/access-levels', data);
  }
  async createPoll(
    roomId: number,
    question: string,
    options: { id: string; text: string }[] | string[],
    multiple = false
  ): Promise<any> {
    const normalized =
      Array.isArray(options) && options.length > 0 && typeof options[0] === 'string'
        ? (options as string[]).map((text, i) => ({
            id: String.fromCharCode(97 + i),
            text,
          }))
        : options;
    return this.post(`/api/v1/chat/rooms/${roomId}/messages/poll`, {
      question,
      options: normalized,
      multiple,
    });
  }
  async createTemplateFromPreset(presetId: number, name?: string, customize: boolean = false): Promise<{
    template_id: number;
    name: string;
    item_count: number;
    block_count: number;
    can_customize: boolean;
  }> {
    return this.post('/api/v1/checklists/templates/from-preset', {
      preset_id: presetId,
      name,
      customize,
    });
  }
  async deleteAccessLevel(id: number): Promise<any> {
    return this.delete(`/api/v1/access-levels/${id}`);
  }
  async getAbsences(params: Record<string, any> = {}): Promise<any> {
    return this.get(`/api/v1/absences${buildQueryString(params)}`);
  }
  async getAbsencesStats(params: Record<string, any> = {}): Promise<any> {
    return this.get(`/api/v1/absences/stats${buildQueryString(params)}`);
  }
  async getAccessLevels(): Promise<any[]> {
    return this.get('/api/v1/access-levels');
  }
  async getCriticalFailures(limit = 20): Promise<any> {
    return this.get(`/api/v1/compliance/critical-failures${buildQueryString({ limit })}`);
  }
  async getCurrentUser(): Promise<any> {
    return this.get('/api/v1/auth/me');
  }
  async getLinkPreview(url: string): Promise<any> {
    return this.get(`/api/v1/chat/link-preview${buildQueryString({ url })}`);
  }
  async getMyProductUnreadCount(): Promise<{ count: number }> {
    return this.get<{ count: number }>('/api/v1/product-updates/my/unread-count');
  }
  async getRequestParticipants(requestId: number, includeInactive: boolean = false): Promise<any[]> {
    return this.get(`/api/v1/consumables/requests/${requestId}/participants${buildQueryString({ include_inactive: includeInactive })}`);
  }
  async getUpcomingAbsences(daysBefore = 7): Promise<any> {
    return this.get(`/api/v1/absences/reminders/upcoming${buildQueryString({ days_before: daysBefore })}`);
  }
  async getUsers(params: Record<string, any> = {}): Promise<any> {
    return this.get(`/api/v1/users${buildQueryString(params)}`);
  }
  async patchUserAccess(
    userId: number,
    data: { role: 'employee' | 'manager' | 'admin'; sync_role_from_positions?: boolean },
  ): Promise<any> {
    return this.patch(`/api/v1/users/${userId}/access`, data);
  }
  // ─── Absences ───────────────────────────────────────────────────────────────
  async processAbsenceReminders(): Promise<any> {
    return this.post('/api/v1/absences/reminders/process', {});
  }
  async removeRequestParticipant(requestId: number, participantId: number): Promise<void> {
    return this.delete(`/api/v1/consumables/requests/${requestId}/participants/${participantId}`);
  }
  async reviewAbsence(id: number, data: any): Promise<any> {
    return this.post(`/api/v1/absences/${id}/review`, data);
  }
  // ─── Salary Rates ───────────────────────────────────────────────────────────
  async revokeDelegation(requestId: number, delegationId: number): Promise<void> {
    return this.delete(`/api/v1/consumables/requests/${requestId}/delegations/${delegationId}`);
  }
  // ─── Product Updates ─────────────────────────────────────────────────────────
  async toggleReaction(messageId: number, emoji: string): Promise<any> {
    return this.put(`/api/v1/chat/messages/${messageId}/reactions`, { emoji });
  }
  async updateAccessLevel(
    id: number,
    data: { name?: string; base_role?: string; modules?: Record<string, unknown>; is_active?: boolean },
  ): Promise<any> {
    return this.patch(`/api/v1/access-levels/${id}`, data);
  }
  async uploadAttachment(
    roomId: number,
    messageId: number,
    file: File,
    options?: {
      onProgress?: (progress: number) => void;
      messageType?: 'voice' | 'video_note';
      duration?: number;
    }
  ): Promise<any> {
    return this.uploadChatAttachment(roomId, messageId, file, options);
  }
  async uploadTopicIcon(
    roomId: number,
    topicId: number,
    file: File
  ): Promise<{ ok?: boolean; icon_url?: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.authedFetch(
      `${this.baseURL}/api/v1/chat/rooms/${roomId}/topics/${topicId}/avatar`,
      { method: 'POST', body: formData }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(response.status, errorData.detail || response.statusText, errorData);
    }
    return response.json();
  }
  async votePoll(pollId: number, optionIds: string[]): Promise<any> {
    return this.post(`/api/v1/chat/polls/${pollId}/vote`, { option_ids: optionIds });
  }
}
